// MarkdownText - Renders AI-generated text with markdown formatting
import React from 'react';
import ReactMarkdown from 'react-markdown';

/**
 * Reusable component for rendering markdown-formatted text
 * with consistent mystical styling across the app.
 */
export const MarkdownText = ({ children, className = '', size = 'base' }) => {
    if (!children) return null;

    const sizeClasses = {
        sm: 'text-xs sm:text-sm',
        base: 'text-sm sm:text-base',
        lg: 'text-base sm:text-lg'
    };

    return (
        <ReactMarkdown
            className={`prose prose-invert max-w-none leading-relaxed ${sizeClasses[size] || sizeClasses.base} ${className}`}
            components={{
                // Paragraphs with proper spacing
                p: ({ children }) => (
                    <p className="mb-3 last:mb-0 text-amber-100/90">
                        {children}
                    </p>
                ),
                // Bold text - highlighted in amber
                strong: ({ children }) => (
                    <strong className="text-amber-300 font-semibold">
                        {children}
                    </strong>
                ),
                // Italic text - subtle purple
                em: ({ children }) => (
                    <em className="text-purple-200 not-italic font-medium">
                        {children}
                    </em>
                ),
                // Headers - styled consistently
                h1: ({ children }) => (
                    <h1 className="text-xl sm:text-2xl font-serif text-amber-200 mb-3 mt-4 first:mt-0">
                        {children}
                    </h1>
                ),
                h2: ({ children }) => (
                    <h2 className="text-lg sm:text-xl font-serif text-amber-200 mb-2 mt-3 first:mt-0">
                        {children}
                    </h2>
                ),
                h3: ({ children }) => (
                    <h3 className="text-base sm:text-lg font-serif text-amber-200 mb-2 mt-3 first:mt-0">
                        {children}
                    </h3>
                ),
                // Lists
                ul: ({ children }) => (
                    <ul className="list-disc list-inside mb-3 space-y-1 text-amber-100/90">
                        {children}
                    </ul>
                ),
                ol: ({ children }) => (
                    <ol className="list-decimal list-inside mb-3 space-y-1 text-amber-100/90">
                        {children}
                    </ol>
                ),
                li: ({ children }) => (
                    <li className="text-amber-100/90">
                        {children}
                    </li>
                ),
                // Horizontal rule
                hr: () => (
                    <hr className="my-4 border-purple-500/30" />
                ),
                // Block quotes - mystical styling
                blockquote: ({ children }) => (
                    <blockquote className="border-l-2 border-amber-500/50 pl-4 my-3 italic text-purple-200">
                        {children}
                    </blockquote>
                ),
            }}
        >
            {children}
        </ReactMarkdown>
    );
};

export default MarkdownText;
